// ============================================================
//  Bragsheet React — data/profiles.js
//  Firestore reads/writes for the signed-in user's OWN userProfiles doc,
//  using the v11 modular SDK. Mirrors the vanilla firebase.js
//  UserProfiles.get / UserProfiles.update (username only here).
// ============================================================

// Firestore is loaded lazily (see ../firestore.js) so this module doesn't pull
// the heavy firebase/firestore SDK onto the first-render path.
import { loadFirestore } from '../firestore.js';
import { queueableWrite, friendlySaveError } from '../utils/offline.js';

// Same rule as the vanilla sanitiseUsername: alphanumeric + underscore only,
// lowercased, capped at 30 chars.
export function sanitiseUsername(value) {
  if (typeof value !== 'string') return '';
  return value.replace(/[^a-zA-Z0-9_]/g, '').toLowerCase().substring(0, 30);
}

// Port of the vanilla firebase.js sanitiseText (also used in data/wins.js):
// strip HTML tags + dangerous chars, trim, cap length.
function sanitiseText(value, maxLength = 200) {
  if (typeof value !== 'string') return '';
  return value.replace(/<[^>]*>/g, '').replace(/[<>'"]/g, '').trim().substring(0, maxLength);
}

/**
 * Create a new user's profile doc on signup. Mirrors the vanilla
 * UserProfiles.create (firebase.js) shape EXACTLY so web / Android / MCP all
 * read one schema. Called once, right after the Firebase Auth account is
 * created (email signup, or a new Google user — see AuthContext).
 *
 * Deliberately does NOT set `onboardedAt`: a fresh signup has createdAt well
 * after the onboarding migration cutoff and no onboardedAt, so the onboarding
 * gate (see data/onboarding.js) correctly routes it into onboarding.
 *
 * @param {import('firebase/auth').User} user  the freshly-created auth user
 * @param {string} [displayName]  optional name from the signup form
 * @returns {Promise<{success:boolean, profile?:object, error?:string}>}
 */
export async function createUserProfile(user, displayName) {
  if (!user || !user.uid) return { success: false, error: 'No user.' };
  const email = String(user.email || '');
  const profile = {
    userId: user.uid,
    email: sanitiseText(email, 254),
    displayName: sanitiseText(displayName || user.displayName || '', 100),
    username: sanitiseUsername(email.split('@')[0]),
    // Entitlement is server-owned. The client only ever writes `false` here at
    // creation; the Play-verified grant to true happens in the Cloud Function.
    isPro: false,
    streak: 0,
    lastWinDate: null,
  };
  try {
    const { db, doc, setDoc, serverTimestamp } = await loadFirestore();
    await setDoc(doc(db, 'userProfiles', user.uid), {
      ...profile,
      createdAt: serverTimestamp(),
    });
    return { success: true, profile };
  } catch (err) {
    console.error('[Profiles] Create failed:', err);
    return { success: false, error: friendlySaveError(err) };
  }
}

/**
 * Read the user's profile doc (isPro, username, streak, …), or null.
 * @param {string} userId
 * @returns {Promise<object|null>}
 */
export async function getUserProfile(userId) {
  const uid = String(userId || '').trim();
  if (!uid) return null;
  try {
    const { db, doc, getDoc } = await loadFirestore();
    const snap = await getDoc(doc(db, 'userProfiles', uid));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (err) {
    console.error('[Profiles] Get failed:', err);
    throw err;
  }
}

/**
 * Update the user's username on their own profile doc. The user's own
 * straightforward CRUD; the value is sanitised before writing.
 * @param {string} userId
 * @param {string} username
 * @returns {Promise<{success:boolean, error?:string}>}
 */
export async function updateUsername(userId, username) {
  const uid = String(userId || '').trim();
  const safe = sanitiseUsername(username);
  if (!uid || !safe) return { success: false, error: 'Invalid username.' };
  try {
    const { db, doc, updateDoc } = await loadFirestore();
    // Category A (own profile): queue offline; online failure still rejects.
    const { queued } = await queueableWrite(
      () => updateDoc(doc(db, 'userProfiles', uid), { username: safe }),
      { label: 'username' },
    );
    return { success: true, username: safe, queued };
  } catch (err) {
    console.error('[Profiles] Update username failed:', err);
    return { success: false, error: friendlySaveError(err) };
  }
}

/**
 * Durable notification-preference sync — writes the canonical notifications
 * object into the user's own userProfiles doc (merge), mirroring the vanilla
 * UserProfiles.saveNotifications. Called on every preference change by the
 * settings-notifications screen.
 * @param {string} userId
 * @param {{on:boolean, wrapUpTime:{hour:number,minute:number,ampm:string}, frequency:string, days:string[]}} notifications
 * @returns {Promise<{success:boolean, error?:string}>}
 */
export async function saveNotifications(userId, notifications) {
  const uid = String(userId || '').trim();
  if (!uid || !notifications || typeof notifications !== 'object') {
    return { success: false, error: 'Invalid input.' };
  }
  try {
    const { db, doc, setDoc } = await loadFirestore();
    await setDoc(doc(db, 'userProfiles', uid), { notifications }, { merge: true });
    return { success: true };
  } catch (err) {
    console.error('[Profiles] saveNotifications failed:', err);
    return { success: false, error: friendlySaveError(err) };
  }
}

/**
 * Record that the user has seen the first-visit welcome modal (shown once on the
 * dashboard after onboarding, in place of the old "You're all set" page). The
 * flag lives on userProfiles so the modal never reappears on refresh, relaunch,
 * or another device. Requires `hasSeenWelcome` in firestore.rules' profile
 * allowlist. Best-effort: fire-and-forget from the dashboard.
 * @param {string} userId
 * @returns {Promise<{success:boolean, error?:string}>}
 */
export async function markWelcomeSeen(userId) {
  const uid = String(userId || '').trim();
  if (!uid) return { success: false, error: 'Not signed in.' };
  try {
    const { db, doc, setDoc } = await loadFirestore();
    await setDoc(doc(db, 'userProfiles', uid), { hasSeenWelcome: true }, { merge: true });
    return { success: true };
  } catch (err) {
    console.error('[Profiles] markWelcomeSeen failed:', err);
    return { success: false, error: friendlySaveError(err) };
  }
}
